import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { formatEur, formatDate } from '../../lib/format';
import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api';
import styles from './CheckoutConfirmationPage.module.scss';

type MbwayState = 'pending' | 'paid' | 'failed';

interface PaymentStatusResponse {
  status: MbwayState;
}

const POLL_INTERVAL_MS = 3000;

export default function CheckoutConfirmationPage(): JSX.Element {
  const [sp] = useSearchParams();
  const orderNumber = sp.get('order');
  const method = sp.get('method');
  const entity = sp.get('entity');
  const reference = sp.get('reference');
  const expires = sp.get('expires');
  const amount = sp.get('amount');
  const status = sp.get('status');
  const easypayId = sp.get('pid');
  const isPickup = sp.get('pickup') === '1';
  const pickupName = sp.get('pickupName');
  const pickupAddress = sp.get('pickupAddress');
  const pickupDeadline = sp.get('pickupDeadline');

  const error = status === 'error' || status === 'cancel';
  const isMbway = method === 'mbway';

  const expiresMs = isMbway && expires ? Date.parse(expires) : null;
  const [mbwayState, setMbwayState] = useState<MbwayState>('pending');
  const [remainingMs, setRemainingMs] = useState<number>(() =>
    expiresMs ? Math.max(0, expiresMs - Date.now()) : 0
  );

  // Tick the countdown every second.
  useEffect(() => {
    if (!isMbway || !expiresMs || mbwayState !== 'pending') return;
    const id = window.setInterval(() => {
      const left = Math.max(0, expiresMs - Date.now());
      setRemainingMs(left);
    }, 1000);
    return () => window.clearInterval(id);
  }, [isMbway, expiresMs, mbwayState]);

  // Poll the server for payment status while pending.
  const stoppedRef = useRef(false);
  useEffect(() => {
    if (!isMbway || !easypayId || mbwayState !== 'pending') return;
    stoppedRef.current = false;
    let timer: number | null = null;

    const tick = async () => {
      if (stoppedRef.current) return;
      try {
        const res = await api.get<PaymentStatusResponse>(
          `/checkout/payment-status/${encodeURIComponent(easypayId)}`
        );
        const next = res.data.status;
        if (next === 'paid' || next === 'failed') {
          setMbwayState(next);
          stoppedRef.current = true;
          return;
        }
      } catch {
        // Swallow transient errors; we'll try again on the next tick.
      }
      // Stop polling 60s past the deadline — webhook will land eventually,
      // but the user has likely abandoned by then.
      if (expiresMs && Date.now() > expiresMs + 60_000) {
        setMbwayState('failed');
        stoppedRef.current = true;
        return;
      }
      timer = window.setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer = window.setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      stoppedRef.current = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [isMbway, easypayId, expiresMs, mbwayState]);

  // If the countdown hits zero and we still don't know, treat as failed.
  useEffect(() => {
    if (!isMbway || mbwayState !== 'pending' || !expiresMs || remainingMs > 0) {
      return undefined;
    }
    // One last poll attempt happens in the polling effect; if it doesn't
    // succeed within 60s, polling itself flips to failed. Here we just
    // surface the expiry to the user immediately when the clock hits 0.
    const id = window.setTimeout(() => {
      if (!stoppedRef.current) setMbwayState('failed');
    }, 5000);
    return () => window.clearTimeout(id);
  }, [isMbway, mbwayState, expiresMs, remainingMs]);

  return (
    <>
      <SEO title="Confirmação de encomenda" noindex />
      <div className={`container ${styles.wrapper}`}>
        {error ? (
          <div className={styles.card}>
            <h1 className={styles.title}>Pagamento não concluído</h1>
            <p>
              Não foi possível confirmar o teu pagamento. Podes tentar novamente
              a partir da tua conta.
            </p>
            <div className={styles.actions}>
              <Button variant="primary" size="lg" to="/conta">
                Ver as minhas encomendas
              </Button>
            </div>
          </div>
        ) : isMbway && mbwayState === 'paid' ? (
          <div className={styles.card}>
            <h1 className={styles.title}>{t.checkout.mbwayPaidTitle}</h1>
            {orderNumber ? (
              <p>
                {t.checkout.orderNumber}: <strong>{orderNumber}</strong>
              </p>
            ) : null}
            {amount ? (
              <p>
                Total: <strong>{formatEur(amount)}</strong>
              </p>
            ) : null}
            <p>{t.checkout.mbwayPaidDescription}</p>
            <div className={styles.actions}>
              <Button variant="primary" to="/">
                Voltar à loja
              </Button>
              <Button variant="secondary" to="/conta">
                As minhas encomendas
              </Button>
            </div>
          </div>
        ) : isMbway && mbwayState === 'failed' ? (
          <div className={styles.card}>
            <h1 className={styles.title}>{t.checkout.mbwayExpiredTitle}</h1>
            {orderNumber ? (
              <p>
                {t.checkout.orderNumber}: <strong>{orderNumber}</strong>
              </p>
            ) : null}
            <p>{t.checkout.mbwayExpiredDescription}</p>
            <div className={styles.actions}>
              <Button variant="primary" size="lg" to="/conta">
                Ver as minhas encomendas
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.card}>
            <h1 className={styles.title}>{t.checkout.thankYou}</h1>
            {orderNumber ? (
              <p>
                {t.checkout.orderNumber}: <strong>{orderNumber}</strong>
              </p>
            ) : null}
            {amount ? (
              <p>
                Total: <strong>{formatEur(amount)}</strong>
              </p>
            ) : null}

            {isMbway ? (
              <>
                <p className={styles.instruction}>{t.checkout.mbwayPending}</p>
                <p className={styles.muted}>{t.checkout.mbwayWindowHint}</p>
                {expiresMs ? (
                  <div className={styles.timer} aria-live="polite">
                    <span className={styles.timerLabel}>
                      {t.checkout.mbwayTimerLabel}
                    </span>
                    <span className={styles.timerValue}>
                      {formatRemaining(remainingMs)}
                    </span>
                  </div>
                ) : null}
                {easypayId ? (
                  <p className={styles.muted}>{t.checkout.mbwayPolling}</p>
                ) : null}
              </>
            ) : null}

            {method === 'multibanco' && entity && reference ? (
              <div className={styles.mb}>
                <h2 className={styles.mbTitle}>{t.checkout.multibancoRefTitle}</h2>
                <dl>
                  <dt>{t.checkout.multibancoEntity}</dt>
                  <dd>{entity}</dd>
                  <dt>{t.checkout.multibancoReference}</dt>
                  <dd>{reference}</dd>
                  {amount ? (
                    <>
                      <dt>{t.checkout.multibancoAmount}</dt>
                      <dd>{formatEur(amount)}</dd>
                    </>
                  ) : null}
                  {expires ? (
                    <>
                      <dt>Válido até</dt>
                      <dd>{formatDate(expires)}</dd>
                    </>
                  ) : null}
                </dl>
                <p>Podes pagar em qualquer caixa Multibanco ou pelo homebanking.</p>
              </div>
            ) : null}

            {method === 'credit_card' ? (
              <p>O teu pagamento está a ser processado.</p>
            ) : null}

            {isPickup ? (
              <div className={styles.mb}>
                <h2 className={styles.mbTitle}>{t.checkout.pickupConfirmTitle}</h2>
                <dl>
                  {pickupName ? (
                    <>
                      <dt>Local</dt>
                      <dd>{pickupName}</dd>
                    </>
                  ) : null}
                  {pickupAddress ? (
                    <>
                      <dt>Morada</dt>
                      <dd>{pickupAddress}</dd>
                    </>
                  ) : null}
                  {pickupDeadline ? (
                    <>
                      <dt>{t.checkout.pickupDeadlineLabel}</dt>
                      <dd>{formatDate(pickupDeadline)}</dd>
                    </>
                  ) : null}
                </dl>
              </div>
            ) : null}

            <p className={styles.muted}>
              Vais receber um email com os detalhes da tua encomenda em breve.
            </p>
            <div className={styles.actions}>
              <Button variant="primary" to="/">
                Voltar à loja
              </Button>
              <Button variant="secondary" to="/conta">
                As minhas encomendas
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
